import { useCallback, useRef } from 'react';

import { useLayoutStore } from '../../store/layoutStore';
import { useCollapsiblePane } from './useCollapsiblePane';

const REPLAY_COLLECTIONS_MIN_WIDTH_PX = 220;
const REPLAY_COLLECTIONS_MAX_WIDTH_PX = 480;
const REPLAY_COLLECTIONS_COLLAPSED_WIDTH_PX = 36;
const REPLAY_COLLECTIONS_SNAP_THRESHOLD_PX = 160;
const REPLAY_COLLECTIONS_CONTENT_MIN_WIDTH_PX = 520;
const DEFAULT_REPLAY_COLLECTIONS_SIZE_PERCENT = 18;

export function useReplayCollectionsLayout() {
  const isReplayCollectionsCollapsed = useLayoutStore(
    (state) => state.isReplayCollectionsCollapsed,
  );
  const setReplayCollectionsCollapsed = useLayoutStore(
    (state) => state.setReplayCollectionsCollapsed,
  );
  const replayCollectionsPanelSizes = useLayoutStore(
    (state) => state.replayCollectionsPanelSizes,
  );
  const setReplayCollectionsPanelSizes = useLayoutStore(
    (state) => state.setReplayCollectionsPanelSizes,
  );
  const replayCollectionsPanelSizesRef = useRef(replayCollectionsPanelSizes);
  replayCollectionsPanelSizesRef.current = replayCollectionsPanelSizes;

  const handleResizeEnd = useCallback(
    (collectionsSize: number, mainSize: number, shouldCollapse: boolean) => {
      setReplayCollectionsPanelSizes(
        replayCollectionsPanelSizesRef.current.map((panelEntry) =>
          panelEntry.id === 'collections'
            ? {
                ...panelEntry,
                size: shouldCollapse ? panelEntry.size : collectionsSize,
              }
            : panelEntry.id === 'main'
              ? { ...panelEntry, size: mainSize }
              : panelEntry,
        ),
      );
    },
    [setReplayCollectionsPanelSizes],
  );

  return useCollapsiblePane({
    paneId: 'collections',
    contentId: 'main',
    panePosition: 'start',
    minWidthPx: REPLAY_COLLECTIONS_MIN_WIDTH_PX,
    maxWidthPx: REPLAY_COLLECTIONS_MAX_WIDTH_PX,
    minContentWidthPx: REPLAY_COLLECTIONS_CONTENT_MIN_WIDTH_PX,
    collapsedWidthPx: REPLAY_COLLECTIONS_COLLAPSED_WIDTH_PX,
    snapThresholdPx: REPLAY_COLLECTIONS_SNAP_THRESHOLD_PX,
    persistedSize:
      replayCollectionsPanelSizes.find(
        (panelEntry) => panelEntry.id === 'collections',
      )?.size ?? DEFAULT_REPLAY_COLLECTIONS_SIZE_PERCENT,
    isCollapsed: isReplayCollectionsCollapsed,
    onCollapseChange: setReplayCollectionsCollapsed,
    onResizeEnd: handleResizeEnd,
  });
}
